import { AppCombobox, AppDialog, AppInput, AppSelectPopover, AppTextarea } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { INQUIRY_ASSIGN_MEMBER_MESSAGES } from 'shared'
import { AppWorkLocationCheckboxGroup } from '@/components/AppWorkLocationCheckboxGroup'
import { useFormattedNumberInput } from '@/hooks/useFormattedNumberInput'
import { MemberCombobox } from '../../../../../MemberCombobox'
import { useCreateInquiryAssign } from '../../hooks/useCreateInquiryAssign'
import { useUpdateInquiryAssign } from '../../hooks/useUpdateInquiryAssign'
import type { InquiryAssignLocal } from '../../types'
import { useAssignSaveDialog } from './hooks/useAssignSaveDialog'

interface AssignSaveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inquiryId: number
  assign?: InquiryAssignLocal | null
  onSaveSuccess?: (savedAssignStatusId: number) => void
}

export function AssignSaveDialog({ open, onOpenChange, inquiryId, assign, onSaveSuccess }: AssignSaveDialogProps) {
  const {
    isEdit,
    inquiryAssignStatusId,
    setInquiryAssignStatusId,
    roleId,
    setRoleId,
    memberId,
    setMemberId,
    unitPrice,
    setUnitPrice,
    assignPersonMonth,
    setAssignPersonMonth,
    assignedOn,
    setAssignedOn,
    notes,
    setNotes,
    workLocations,
    setWorkLocations,
    selectedEmployeeTypeName,
    statusBadgeOptions,
    roleGroups,
    isRolesLoading,
    memberOptions,
    isMembersLoading,
    memberNotice,
    formErrors,
    hasErrors,
    validate,
    handleClose,
    buildAssignData,
  } = useAssignSaveDialog({ open, onOpenChange, inquiryId, assign })

  const handleSaveSuccess = () => {
    handleClose()
    onSaveSuccess?.(inquiryAssignStatusId)
  }
  const { mutate: createMutate, isPending: isCreating } = useCreateInquiryAssign(inquiryId, {
    onSuccess: handleSaveSuccess,
  })
  const { mutate: updateMutate, isPending: isUpdating } = useUpdateInquiryAssign(inquiryId, {
    onSuccess: handleSaveSuccess,
  })
  const isPending = isCreating || isUpdating

  const unitPriceInputProps = useFormattedNumberInput(unitPrice, setUnitPrice)

  const handleSave = () => {
    if (!validate()) return
    const data = buildAssignData()
    const payload = {
      memberId: data.member_id,
      roleId: data.role_id,
      unitPrice: data.unit_price,
      assignedOn: data.assigned_on,
      assignPersonMonth: data.assign_person_month,
      salesStatusId: data.sales_status_id,
      inquiryAssignStatusId: data.inquiry_assign_status_id,
      isOnsite: data.is_onsite,
      isOffsite: data.is_offsite,
      isRemote: data.is_remote,
      notes: data.notes,
    }

    if (isEdit && assign?.id) {
      updateMutate({ ...payload, inquiryAssignId: assign.id })
    } else {
      createMutate(payload)
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`アサイン${isEdit ? '編集' : '追加'}`}
      className='max-w-150'
      footerOptions={[
        { label: 'キャンセル', onClick: handleClose, variant: 'ghost', disabled: isPending },
        { label: isEdit ? '更新' : '追加', onClick: handleSave, disabled: isPending || hasErrors },
      ]}
    >
      <div className='max-h-[60vh] space-y-4 overflow-y-auto py-4'>
        <div className='grid grid-cols-2 gap-4'>
          {/* アサイン状況 */}
          <AppSelectPopover
            label='アサイン状況'
            currentValue={inquiryAssignStatusId.toString()}
            options={statusBadgeOptions}
            onSelect={(value) => setInquiryAssignStatusId(Number(value))}
            header='アサイン状況を変更'
            triggerVariant='button'
          />

          {/* 役割 */}
          <AppCombobox
            field={{ label: '役割' }}
            placeholder='役割を選択'
            value={roleId}
            onValueChange={setRoleId}
            groups={roleGroups}
            clearable={!!roleId}
            loading={isRolesLoading}
            search={{ placeholder: '役割名で検索...', emptyMessage: '役割が見つかりません' }}
          />
        </div>

        {/* メンバー(主管部門・関連部門) */}
        <MemberCombobox
          value={memberId}
          onValueChange={setMemberId}
          options={memberOptions}
          loading={isMembersLoading}
          label='メンバー(主管部門・関連部門)'
          placeholder='主管部門・関連部門からメンバーを選択'
          clearable={!!memberId}
          message={memberNotice.message}
          messageVariant={memberNotice.variant}
          emptyMessage={INQUIRY_ASSIGN_MEMBER_MESSAGES.SEARCH_NO_MATCH}
          allowTba
        />

        {/* 雇用形態（読み取り専用） */}
        <div className='w-[calc(50%-0.5rem)]'>
          <span className='text-xs font-medium text-muted-foreground'>雇用形態</span>
          <div className='h-9 w-full rounded-md border border-gray-400 bg-muted/50 px-3 py-1 text-sm leading-7'>
            {selectedEmployeeTypeName ?? '-'}
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          {/* 単価 */}
          <AppInput
            id='assign-unit-price'
            label='単価（円）'
            {...unitPriceInputProps}
            placeholder='0'
            error={formErrors.unitPrice}
          />

          {/* 人月 */}
          <AppInput
            id='assign-person-month'
            label='人月'
            type='number'
            value={assignPersonMonth}
            onChange={(e) => setAssignPersonMonth(e.target.value)}
            min={0}
            max={1.5}
            step={0.01}
            placeholder='1.00'
            error={formErrors.assignPersonMonth}
          />
        </div>

        {/* 参画予定日 */}
        <AppInput
          id='assign-assigned-on'
          label='参画予定日'
          required
          type='month'
          value={assignedOn}
          onChange={(e) => setAssignedOn(e.target.value)}
          error={formErrors.assignedOn}
        />

        {/* コメント */}
        <AppTextarea
          id='assign-notes'
          label='コメント'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder='アサイン条件やメモを入力'
          error={formErrors.notes}
        />

        {/* 作業場所 */}
        <AppWorkLocationCheckboxGroup label='作業場所' value={workLocations} onChange={setWorkLocations} />
      </div>
    </AppDialog>
  )
}
